'use client';

import * as React from 'react';
import { FieldValues, FieldPath, Control, UseControllerProps } from 'react-hook-form';

import { FormInput, FormInputProps } from './form-input';
import { FormTextarea, FormTextareaProps } from './form-textarea';
import { FormSelect, FormSelectProps } from './form-select';
import { FormCheckbox, FormCheckboxProps } from './form-checkbox';
import { FormRadioGroup, FormRadioGroupProps } from './form-radio-group';
import { FormSwitch, FormSwitchProps } from './form-switch';
import { FormNumberInput, FormNumberInputProps } from './form-number-input';

// Base props that all field types share
interface BaseFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];
}

// Discriminated union for different field types
export type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = BaseFormFieldProps<TFieldValues, TName> &
  (
    | ({ fieldType: 'input' } & Omit<
        FormInputProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'textarea' } & Omit<
        FormTextareaProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'select' } & Omit<
        FormSelectProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'checkbox' } & Omit<
        FormCheckboxProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'radio' } & Omit<
        FormRadioGroupProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'switch' } & Omit<
        FormSwitchProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
    | ({ fieldType: 'number' } & Omit<
        FormNumberInputProps<TFieldValues, TName>,
        keyof BaseFormFieldProps<TFieldValues, TName>
      >)
  );

/**
 * Universal form field component that renders the appropriate field type
 * based on the fieldType prop. Provides a single interface for all form fields.
 *
 * @example
 * ```tsx
 * <FormField
 *   fieldType="input"
 *   name="email"
 *   control={control}
 *   label="Email Address"
 *   type="email"
 *   rules={{ required: 'Email is required' }}
 * />
 *
 * <FormField
 *   fieldType="select"
 *   name="type"
 *   control={control}
 *   label="Customer Type"
 *   options={[
 *     { value: 'corporate', label: 'Corporate' },
 *     { value: 'individual', label: 'Individual' }
 *   ]}
 *   rules={{ required: 'Please select a type' }}
 * />
 * ```
 */
export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: FormFieldProps<TFieldValues, TName>) {
  const { fieldType, name, control, rules, ...fieldProps } = props;

  switch (fieldType) {
    case 'input':
      return (
        <FormInput
          name={name}
          control={control}
          rules={rules}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'textarea':
      return (
        <FormTextarea
          name={name}
          control={control}
          rules={rules}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'select':
      return (
        <FormSelect
          name={name}
          control={control}
          rules={rules}
          options={[]}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'checkbox':
      return (
        <FormCheckbox
          name={name}
          control={control}
          rules={rules}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'radio':
      return (
        <FormRadioGroup
          name={name}
          control={control}
          rules={rules}
          options={[]}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'switch':
      return (
        <FormSwitch
          name={name}
          control={control}
          rules={rules}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    case 'number':
      return (
        <FormNumberInput
          name={name}
          control={control}
          rules={rules}
          {...(fieldProps as Record<string, unknown>)}
        />
      );

    default: {
      // TypeScript exhaustiveness check
      const _exhaustiveCheck: never = fieldType;
      return _exhaustiveCheck;
    }
  }
}

FormField.displayName = 'FormField';
